import { useContext } from "react";
import FeatureContext from "../context/FeatureContext";

export const useFeatureFlaggingState = () => {
  const { flags, setFlags, context } = useContext(FeatureContext);

  return {
    flags,
    context,
    setFlags,
  };
};

/**
 * Evaluates a single flag. Pure — no React, no data fetching — so it is directly
 * unit testable and has exactly one dynamic branch regardless of how many
 * data-dependent flags exist.
 *
 * A flag is gated by release stage first. If it clears that gate it is either:
 *  - static (no predicate)        -> enabled
 *  - data-dependent (`isEnabled`) -> `isEnabled(deps)` decides. Predicates are
 *    expected to return false while their inputs are still `undefined`
 *    (loading), then flip true once the data resolves.
 *
 * @param {object} flag           A flag definition from FeatureProvider's FLAGS.
 * @param {object} entityContext  The entity's context, e.g. { availableStages }.
 * @param {object} [deps]         Async-loaded data for data-dependent flags.
 */
export const evaluateFlag = (flag, entityContext, deps = {}) => {
  try {
    if (!flag || !entityContext) {
      return false;
    }

    if (!entityContext.availableStages?.includes(flag.stage)) {
      return false;
    }

    return flag.isEnabled ? !!flag.isEnabled(deps) : true;
  } catch (error) {
    console.error(error);

    // DEFAULT
    return false;
  }
};

/**
 * Flags are resolved once, centrally, by FeatureProvider. Reading one is
 * therefore a plain lookup — callers never need to know what data a flag
 * depends on, which is what lets a caller evaluate a flag by a variable key
 * (e.g. the matched route's flag) without branching per flag.
 */
const useResolvedFlag = (flagKey) => {
  const { resolvedFlags } = useContext(FeatureContext);

  return resolvedFlags?.[flagKey];
};

export const useCheckFeatureFlag = (flagKey) => {
  const resolvedFlag = useResolvedFlag(flagKey);

  // Gentle fallback for routes/components with no flag
  if (!flagKey || flagKey === "SKIP") {
    return true;
  }

  // Unresolved (unknown flag, or used outside the provider) is default-deny
  return resolvedFlag?.isEnabled ?? false;
};

/**
 * Same result as useCheckFeatureFlag, plus whether the flag's dependencies are
 * still loading. Useful to render a placeholder instead of letting a feature's
 * entry point pop in once its data resolves.
 */
export const useFeatureFlagStatus = (flagKey) => {
  const resolvedFlag = useResolvedFlag(flagKey);

  if (!flagKey || flagKey === "SKIP") {
    return { isEnabled: true, isResolving: false };
  }

  return {
    isEnabled: resolvedFlag?.isEnabled ?? false,
    isResolving: resolvedFlag?.isResolving ?? false,
  };
};
